<script setup lang="ts">
import { onBeforeUpdate, onUpdated, ref } from 'vue'
import type { ListPagerChange, ListPagerProps } from './list-pager/types'
import { useListPager } from './list-pager/useListPager'

defineOptions({ inheritAttrs: false })
const props = defineProps<ListPagerProps>()
const emit = defineEmits<{ change: [value: ListPagerChange] }>()
const { sizes, size, pages, totalPages, outOfRange, current, from, to, visible, go, resize } = useListPager(props, value => emit('change', value))
const recovery = ref<HTMLElement>()
const navigation = ref<HTMLElement>()

let focusedStep: HTMLButtonElement | undefined
onBeforeUpdate(() => {
  const active = document.activeElement
  focusedStep = active instanceof HTMLButtonElement && navigation.value?.contains(active) ? active : undefined
})
onUpdated(() => {
  const previous = focusedStep
  focusedStep = undefined
  if (!previous?.disabled || !navigation.value) return
  if (document.activeElement !== previous && document.activeElement !== document.body) return
  const direction = previous.getAttribute('aria-label') === props.labels.previous || previous.getAttribute('aria-label') === props.labels.first
    ? props.labels.next : props.labels.previous
  const target = [...navigation.value.querySelectorAll<HTMLButtonElement>('.fw-list-pager-step')]
    .find(button => !button.disabled && button.getAttribute('aria-label') === direction) ?? navigation.value
  if (target === navigation.value) target.tabIndex = -1
  target.focus({ preventScroll: true })
})
function returnToFirst() {
  // Move focus before navigation can replace the list with its loading state.
  let container = recovery.value?.parentElement
  let heading: HTMLElement | null = null
  while (container && container !== document.body && !heading) {
    heading = container.querySelector<HTMLElement>('h1, h2, [role="heading"]')
    container = container.parentElement
  }
  const target = heading ?? navigation.value ?? recovery.value
  if (target) {
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1')
    target.focus({ preventScroll: true })
  }
  go(1)
}
</script>

<template>
  <div
    v-if="outOfRange"
    ref="recovery"
    class="fw-list-pager-recovery"
    role="status"
    data-list-state="out-of-range"
    data-bee-state="out-of-range"
  >
    <b>{{ labels.outOfRangeTitle }}</b>
    <span>{{ labels.outOfRange(totalPages) }}</span>
    <button
      type="button"
      @click="returnToFirst"
    >
      {{ labels.returnToFirst }}
    </button>
  </div>
  <nav
    v-if="visible"
    ref="navigation"
    v-bind="$attrs"
    class="fw-list-pager"
    data-bee-component="list-pager"
    :aria-label="labels.navigation"
  >
    <span
      v-if="!outOfRange"
      class="fw-list-pager-line"
      aria-live="polite"
    >{{ labels.range(from, to, totalCount) }}{{ filtered && totalUnfiltered != null ? ` ${labels.unfiltered(totalUnfiltered)}` : '' }}</span>
    <span>{{ labels.perPage }}</span>
    <div
      class="fw-list-pager-sizes"
      role="group"
      :aria-label="labels.perPage"
    >
      <button
        v-for="option in sizes"
        :key="option"
        data-bee-component="segment"
        type="button"
        :aria-pressed="option === size"
        @click="resize(option)"
      >
        {{ option }}
      </button>
    </div>
    <button
      type="button"
      class="fw-list-pager-step"
      :aria-label="labels.first"
      :title="labels.first"
      :disabled="page <= 1"
      :aria-disabled="page <= 1"
      @click="go(1)"
    >
      «
    </button>
    <button
      type="button"
      class="fw-list-pager-step"
      :aria-label="labels.previous"
      :title="labels.previous"
      :disabled="page <= 1"
      :aria-disabled="page <= 1"
      @click="go(Math.min(page - 1, pages))"
    >
      ‹
    </button>
    <span class="fw-list-pager-of">{{ labels.pageOf(current, pages) }}</span>
    <button
      type="button"
      class="fw-list-pager-step"
      :aria-label="labels.next"
      :title="labels.next"
      :disabled="current >= pages"
      :aria-disabled="current >= pages"
      @click="go(current + 1)"
    >
      ›
    </button>
    <button
      type="button"
      class="fw-list-pager-step"
      :aria-label="labels.last"
      :title="labels.last"
      :disabled="current >= pages"
      :aria-disabled="current >= pages"
      @click="go(pages)"
    >
      »
    </button>
  </nav>
</template>

<style src="./list-pager.css"></style>
